/**
 * Global error reporter.
 *
 * Captures `window.error` and `unhandledrejection` events so async failures
 * (which React's ErrorBoundary cannot catch) are logged consistently and
 * can be forwarded to a monitoring backend in the future.
 *
 * The intent is to give the app a single integration point for wiring up
 * Sentry / Datadog / a custom endpoint later without hunting down every
 * `.catch(console.error)` in the codebase.
 */

const MAX_REPORT_QUEUE = 50;
const recentReports = [];
const listeners = new Set();
let installed = false;

function summarize(error) {
  if (!error) return { message: "Unknown error" };
  if (error instanceof Error) {
    return {
      message: error.message,
      name: error.name,
      stack: error.stack
    };
  }
  if (typeof error === "string") {
    return { message: error };
  }
  try {
    return { message: JSON.stringify(error) };
  } catch {
    return { message: String(error) };
  }
}

function record(source, error) {
  const entry = {
    source,
    timestamp: Date.now(),
    ...summarize(error)
  };

  recentReports.push(entry);
  if (recentReports.length > MAX_REPORT_QUEUE) {
    recentReports.shift();
  }

  listeners.forEach((listener) => {
    try {
      listener(entry);
    } catch (listenerError) {
      // Never let a broken listener break other listeners.
      console.error("[netbase] error listener threw", listenerError);
    }
  });

  // Always preserve the original entry in devtools.
  console.error(`[netbase:${source}]`, error);
}

/**
 * Install global error handlers. Safe to call multiple times.
 */
export function installErrorReporter() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("error", (event) => {
    record("window.error", event?.error || event?.message || event);
  });

  window.addEventListener("unhandledrejection", (event) => {
    record("unhandledrejection", event?.reason);
  });
}

/**
 * Subscribe to every reported error. Useful for toasts, banners,
 * or future monitoring integrations.
 */
export function subscribeToErrors(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Manually report an error from catch blocks that shouldn't bubble.
 */
export function reportError(source, error) {
  record(source, error);
}

/**
 * Read the most recent entries (oldest first). Primarily for tests.
 */
export function getRecentErrors() {
  return [...recentReports];
}
